import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { DashboardService } from '../../dashboard.service';
import { DocumentSignatureFlowData, SIGNATURE_TYPES, VALIDATION_STATUSES } from './document-signature-flow-dialog.interface';
import { AuthService } from 'src/app/services/auth.service';
import { environment } from 'src/environments/environment';

@Component({
    selector: 'app-document-signature-flow-dialog',
    templateUrl: './document-signature-flow-dialog.component.html',
    styleUrls: ['./document-signature-flow-dialog.component.scss']
})
export class DocumentSignatureFlowDialogComponent implements OnInit {
    flowData: DocumentSignatureFlowData | null = null;
    isLoading = true;
    isExporting = false;
    errorMessage: string | null = null;

    signatureTypes = SIGNATURE_TYPES;
    validationStatuses = VALIDATION_STATUSES;

    private basePath = environment.interop.basePath;

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: {
            documentGeneratId: number;
            templateName?: string;
        },
        private dialogRef: MatDialogRef<DocumentSignatureFlowDialogComponent>,
        private dashboardService: DashboardService,
        private authService: AuthService
    ) {}

    ngOnInit(): void {
        this.loadFlowData();
    }

    loadFlowData(): void {
        this.isLoading = true;
        this.errorMessage = null;

        this.dashboardService.getDocumentGeneratedSignatureFlow(this.data.documentGeneratId).subscribe({
            next: (response: any) => {
                if (response && response.data) {
                    this.flowData = response.data;
                }
                this.isLoading = false;
            },
            error: (error) => {
                console.error('Error loading signature flow data:', error);
                this.errorMessage = 'Nu s-a putut încărca fluxul de semnături.';
                this.isLoading = false;
            }
        });
    }

    closeDialog(): void {
        this.dialogRef.close();
    }

    getSignTypeIcon(signType: number): string {
        return this.signatureTypes[signType]?.icon || 'edit';
    }

    getSignTypeLabel(signType: number): string {
        return this.signatureTypes[signType]?.label || 'Semnare';
    }

    getSignTypeClass(signType: number): string {
        return this.signatureTypes[signType]?.class || 'sign-type-semnare';
    }

    getStatusIcon(type: number): string {
        return this.validationStatuses[type]?.icon || 'schedule';
    }

    getStatusLabel(type: number): string {
        return this.validationStatuses[type]?.label || 'În așteptare';
    }

    getStatusClass(type: number): string {
        return this.validationStatuses[type]?.class || 'status-pending';
    }

    get hasCompletSignatures(): boolean {
        return !!(this.flowData?.exemplar_complet && this.flowData.exemplar_complet.length > 0);
    }

    get hasCetateanSignatures(): boolean {
        return !!(this.flowData?.exemplar_cetatean && this.flowData.exemplar_cetatean.length > 0);
    }

    formatDate(dateString: string | null): string {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleDateString('ro-RO', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    }

    async exportToPdf(): Promise<void> {
        if (this.isExporting) return;

        this.isExporting = true;

        try {
            const pdfUrl = this.basePath + 'api/registratura/document/generabile/signatures/flow/' + this.data.documentGeneratId + '/pdf';
            const token = await this.authService.getToken();

            const response = await fetch(pdfUrl, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Accept': 'application/pdf'
                }
            });

            if (!response.ok) {
                throw new Error('Network response was not ok');
            }

            const blob = await response.blob();

            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `flux-semnaturi-doc-${this.data.documentGeneratId}.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Error downloading PDF:', error);
        } finally {
            this.isExporting = false;
        }
    }
}
